import { createTheme } from "@mui/material/styles";
import { Config } from "../types";

export default function createMyTheme(config: Config) {
  // Extract colorScheme from config
  const { colorScheme } = config;

  // Create a custom MUI theme using colors from config
  return createTheme({
    palette: {
      primary: {
        main: colorScheme.primary
      },
      secondary: {
        main: colorScheme.secondary
      },
      success: {
        25: "#F6FEF9",
        50: "#ECFDF3",
        100: "#D1FADF",
        200: "#A6F4C5",
        300: "#6CE9A6",
        400: "#32D583",
        500: "#12B76A",
        600: "#039855",
        700: "#027A48",
        800: "#05603A",
        900: "#054F31"
      },
      error: {
        main: "#F04438",
        light: "#FFFBFA",
        25: "#FFFBFA",
        50: "#FEF3F2",
        100: "#FEE4E2",
        200: "#FECDCA",
        300: "#FDA29B",
        400: "#F97066",
        500: "#F04438",
        600: "#D92D20",
        700: "#B42318",
        800: "#912018",
        900: "#7A271A"
      },
      warning: {
        25: "#FFFCF5",
        50: "#FFFAEB",
        100: "#FEF0C7",
        200: "#FEDF89",
        300: "#FEC84B",
        400: "#FDB022",
        500: "#F79009",
        600: "#DC6803",
        700: "#B54708",
        800: "#93370D",
        900: "#7A2E0E"
      },
      grey: {
        //@ts-ignore-next-line add type support for `25` variant
        25: "#FCFCFD",
        50: "#F9FAFB",
        100: "#F2F4F7",
        200: "#EAECF0",
        300: "#D0D5DD",
        400: "#98A2B3",
        500: "#667085",
        600: "#475467",
        700: "#344054",
        800: "#1D2939",
        900: "#101828"
      }
    },
    typography: {
      fontSize: 10, // Set the global font size
      fontFamily: `"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif`,
      h1: {
        fontSize: "1.5rem"
      },
      h2: {
        fontSize: "1.4rem"
      },
      h3: {
        fontSize: "1.3rem"
      },
      h4: {
        fontSize: "1.2rem"
      },
      h5: {
        fontSize: "1.1rem"
      },
      h6: {
        fontSize: "1rem"
      },
      subtitle1: {
        fontSize: "0.875rem"
      },
      subtitle2: {
        fontSize: "0.75rem"
      },
      body1: {
        fontSize: "0.875rem"
      },
      body2: {
        fontSize: "0.75rem"
      },
      button: {
        fontSize: "0.875rem"
      },
      caption: {
        fontSize: "0.75rem"
      },
      overline: {
        fontSize: "0.625rem"
      }
    },
    spacing: 0.5, // Reduce the global spacing
    // ...
    components: {
      MuiTableCell: {
        styleOverrides: {
          root: {
            fontSize: "0.8rem" // Set the default font size for TableCell
          }
        }
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            fontSize: "0.75rem" // Increase font size of Tooltip text
          }
        }
      },

      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: "none", // By default button text is uppercase, override to normal
            borderRadius: "0.5em"
          }
        },
        variants: [
          // secondary button variant with mutated colors
          // @TODO: make dark  dark theme compatible colors
          {
            props: { variant: "secondary" },
            style: ({ theme }) => {
              const isDarkMode = theme.palette.mode === "dark";

              return {
                color: isDarkMode ? theme.palette.grey[200] : theme.palette.grey[500],
                backgroundColor: isDarkMode ? theme.palette.grey[400] : theme.palette.grey[200],
                ":hover": {
                  backgroundColor: isDarkMode ? theme.palette.grey[300] : theme.palette.grey[300]
                }
              };
            }
          },
          {
            props: { variant: "secondary-outlined" },
            style: ({ theme }) => {
              const isDarkMode = theme.palette.mode === "dark";

              return {
                color: isDarkMode ? theme.palette.grey[200] : theme.palette.grey[500],
                border: `1px solid ${isDarkMode ? theme.palette.grey[400] : theme.palette.grey[200]}`,
                ":hover": {
                  backgroundColor: theme.palette.grey[100] || theme.palette.secondary.main
                }
              };
            }
          }
        ]
      }
    }
  });
}
